import { useRef, useEffect } from "react";
import { ProcedureType } from "src/shared/types";
import { USER_PROFILE_API } from "src/constants/api-urls";
import { GenericObject } from "src/defs";
import { CLAIM_TYPES, STATES } from "src/constants";
import { authService, userService, cacheService } from "src/services";
import { normalizeProcedureType } from "src/components/shared/dynamicFilters/helpers";

export function usePreviousProp<T>(prop: T | undefined): T | undefined {
  const ref = useRef<T | undefined>(prop);
  useEffect(() => {
    ref.current = prop;
  }, [prop]);
  return ref.current;
}

/**
 * Resolve effective date range filter field from saved value and allowed options.
 * Prefer saved value if valid, else "created_at" if allowed, else first option.
 */
export function getEffectiveDateRangeFilterField(
  savedValue: string | undefined,
  dateRangeFilterFields: Array<{ value: string }>
): string {
  const allowedValues = dateRangeFilterFields.map((f) => f.value);
  if (savedValue && allowedValues.includes(savedValue)) {
    return savedValue;
  }
  if (allowedValues.includes("created_at")) {
    return "created_at";
  }
  return allowedValues[0] ?? "created_at";
}

/**
 * Get list of procedures
 * @param activeProcedures Active procedures
 */
export const getListOfProcedure = (
  activeProcedures: any
): Array<ProcedureType> => {
  return activeProcedures.map((item: any) => {
    return {
      key: item,
      title: item,
    };
  });
};

/**
 * List of updated procedures
 * @param activeProcedures Active procedures
 * @param procedureType Procedure type
 */
export const getListOfUpdatedProcedure = (
  activeProcedures: any,
  procedureType: any
): Array<ProcedureType> => {
  const procedureKeys = normalizeProcedureType(activeProcedures);
  const proceduresArr = procedureKeys.map((key) => ({ key, title: key }));
  let allExists = proceduresArr.find(
    (procedure: any) => procedure.key === "ALL"
  );
  if (allExists) {
    return procedureType.activeProcedures.map((item: any) => {
      return {
        key: item,
        title: item,
      };
    });
  }
  return proceduresArr;
};

export const getListOfUpdatedClaimType = (claimTypes: Array<any> = []) => {
  return (claimTypes || []).map((item) => {
    let claimTypeConstant = CLAIM_TYPES.find(
      (claimTypeItem) => claimTypeItem.key === item
    );
    return {
      key: item,
      title: claimTypeConstant?.title,
    };
  });
};

export const getListOfUpdatedStateFilter = (stateFilter: Array<any> = []) => {
  return (stateFilter || []).map((item) => {
    let stateFilterConstant = STATES.find(
      (stateFilterItem) => stateFilterItem.key === item
    );
    return {
      key: item,
      title: stateFilterConstant?.title,
    };
  });
};

/**
 * Get object from collection group by keys
 * @param collection
 */
export const getObjectGroupByKey = (collection: Array<any>) => {
  return collection.reduce((acc, current) => {
    if (current) {
      acc[current] = current;
    }
    return acc;
  }, {} as GenericObject);
};

/**
 * Group pendCode by simplified result categories and count frequencies
 * @param pendCodes - Array of pendCode strings
 * @returns Object with simplified result categories as keys and frequencies as values
 */
export const getObjectGroupByPendCode = (pendCodes: Array<string | null>) => {
  return pendCodes.reduce(
    (acc, pendCode) => {
      if (pendCode) {
        // First process pendCode
        let result: string;
        if (["X1", "X5", "X8"].includes(pendCode)) {
          result = pendCode;
        } else {
          result = pendCode.replace(/[0-9]/g, "");
        }

        // Then map to simplified categories
        let simplifiedResult: string;
        switch (result) {
          case "A":
          case "AR":
            simplifiedResult = "A";
            break;
          case "D":
            simplifiedResult = "D";
            break;
          case "CR":
          case "DR":
          case "DG":
          case "X1":
          case "IP":
            simplifiedResult = "CR";
            break;
          case "P":
          case "X5":
          case "X8":
            simplifiedResult = "P";
            break;
          case "R":
            simplifiedResult = "R";
            break;
          case "PA":
            simplifiedResult = "PA";
            break;
          case "PD":
            simplifiedResult = "PD";
            break;
          case "I":
            simplifiedResult = "I";
            break;
          default:
            simplifiedResult = result;
        }

        // Count frequency
        acc[simplifiedResult] = (acc[simplifiedResult] || 0) + 1;
      }
      return acc;
    },
    {} as Record<string, number>
  );
};

/**
 * Sort object by keys alphabetically
 * @param param
 */
export const sortObjectByKeys = (param: GenericObject) => {
  return Object.keys(param)
    .sort()
    .reduce((acc, current) => {
      acc[current] = param[current];
      return acc;
    }, {} as GenericObject);
};

/**
 * Fetch fresh data from my-profile API
 */
export const refreshMyProfile = async () => {
  try {
    const refreshProfile = cacheService.getItemsSync("refreshProfile");
    const accessToken = await authService.getFirebaseToken();
    if (refreshProfile) return;
    const auth = authService.getAuth();
    const clientId = userService.getAuthClientId();
    let headers: any = {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
      "x-oj-client-id": clientId,
    };
    const accountId = cacheService.getItemsSync("accountId");
    if (accountId) {
      headers = {
        ...headers,
        "x-oj-client-id": accountId,
      };
    }
    if (auth?.gcpTenantId) {
      headers = {
        ...headers,
        "X-OJ-TENANT-ID": auth?.gcpTenantId,
      };
    }
    const profile = await fetch(USER_PROFILE_API, {
      headers: headers,
    }).then((res: any) => res.json());
    userService.setUserProfile(profile);
    cacheService.setItemSync("refreshProfile", "1");
    window.location.reload();
  } catch (error) {
    console.error(error);
  }
};
